import { devLog, devWarn, devError } from '../utils/devLogger';

// 토스페이먼츠 브랜드페이 서비스
// 테스트 환경 설정

// 토스페이먼츠 테스트 키 (실제 환경에서는 환경변수로 관리)
export const TOSS_PAYMENTS_CLIENT_KEY = 'test_ck_6BYq7GWPVvxb0J1kyKLX8NE5vbo1'; // 테스트 클라이언트 키
// 주의: SECRET_KEY와 SECURE_KEY는 보안상 서버에서만 사용해야 합니다.
// 클라이언트에서는 CLIENT_KEY만 사용하여 결제 위젯을 띄우는 용도로만 활용합니다.
export const TOSS_PAYMENTS_SECRET_KEY = 'test_sk_ma60RZblrqjQJNROWOl58wzYWBn1'; // 서버 전용
export const TOSS_PAYMENTS_SECURE_KEY = '1da4fe910cee6cdd860c956819a53e6be411f8df56f08f0f53d18ee58a8cb568'; // 서버 전용

// 브랜드페이 설정
export const BRANDPAY_CONFIG = {
  redirectUrl: window.location.origin + '/payment/success',
  failUrl: window.location.origin + '/payment/fail',
  customerKey: 'test_customer_key', // 실제 환경에서는 사용자별 고유 키
};

// 결제수단 타입
export interface PaymentMethod {
  id: string;
  type: 'card' | 'account' | 'phone';
  provider: string;
  name: string;
  number: string;
  expiry?: string;
  isDefault?: boolean;
}

// 토스페이먼츠 브랜드페이 클래스
export class TossPaymentsBrandPay {
  private clientKey: string;

  constructor(clientKey: string) {
    this.clientKey = clientKey;
  }

  // 결제수단 등록
  async addPaymentMethod(): Promise<PaymentMethod> {
    try {
      // 실제 환경에서는 토스페이먼츠 SDK 사용
      // const tossPayments = TossPayments(this.clientKey);
      // const brandpay = tossPayments.brandpay({ redirectUrl: BRANDPAY_CONFIG.redirectUrl });
      // return await brandpay.addPaymentMethod();

      // 테스트 환경 시뮬레이션
      return new Promise((resolve) => {
        setTimeout(() => {
          const testMethod: PaymentMethod = {
            id: 'test_' + Date.now(),
            type: 'card',
            provider: 'toss',
            name: '토스페이 테스트카드',
            number: '**** **** **** ' + Math.floor(1000 + Math.random() * 9000),
            expiry: '12/27',
            isDefault: false,
          };
          resolve(testMethod);
        }, 2000);
      });
    } catch (error) {
      devError('결제수단 등록 실패:', error);
      throw new Error('결제수단 등록에 실패했습니다.');
    }
  }

  // 결제수단 목록 조회
  async getPaymentMethods(): Promise<PaymentMethod[]> {
    try {
      // 실제 환경에서는 API 호출
      // return await api.get('/brandpay/payment-methods');

      // 테스트 데이터 반환
      return [
        {
          id: '1',
          type: 'card',
          provider: 'shinhan',
          name: '신한카드',
          number: '**** **** **** 1234',
          expiry: '12/25',
          isDefault: true,
        },
        {
          id: '2',
          type: 'card',
          provider: 'kb',
          name: '국민카드',
          number: '**** **** **** 5678',
          expiry: '08/26',
          isDefault: false,
        },
        {
          id: '3',
          type: 'account',
          provider: 'toss',
          name: '토스뱅크',
          number: '1000-****-****',
          isDefault: false,
        },
      ];
    } catch (error) {
      devError('결제수단 조회 실패:', error);
      throw new Error('결제수단 조회에 실패했습니다.');
    }
  }

  // 결제수단 삭제
  async deletePaymentMethod(methodId: string): Promise<void> {
    try {
      // 실제 환경에서는 API 호출
      // await api.delete(`/brandpay/payment-methods/${methodId}`);

      // 테스트 환경 시뮬레이션
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve();
        }, 1000);
      });
    } catch (error) {
      devError('결제수단 삭제 실패:', error);
      throw new Error('결제수단 삭제에 실패했습니다.');
    }
  }

  // 기본 결제수단 설정
  async setDefaultPaymentMethod(methodId: string): Promise<void> {
    try {
      // 실제 환경에서는 API 호출
      // await api.put(`/brandpay/payment-methods/${methodId}/default`);

      // 테스트 환경 시뮬레이션
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve();
        }, 1000);
      });
    } catch (error) {
      devError('기본 결제수단 설정 실패:', error);
      throw new Error('기본 결제수단 설정에 실패했습니다.');
    }
  }

  // 결제 요청
  async requestPayment(amount: number, orderId: string, orderName: string, methodId?: string) {
    try {
      devLog('토스 결제 요청:', { amount, orderId, orderName, methodId });
      
      // 토스페이먼츠 SDK 동적 로드
      if (!(window as any).TossPayments) {
        await this.loadTossPaymentsSDK();
      }
      
      const tossPayments = (window as any).TossPayments(this.clientKey);
      
      // 결제 위젯 생성
      const payment = tossPayments.payment({
        amount: amount,
        orderId: orderId,
        orderName: orderName,
        customerName: '구매자',
        customerEmail: 'customer@example.com',
        successUrl: window.location.origin + '/payment/success',
        failUrl: window.location.origin + '/payment/fail'
      });
      
      // 결제창 열기
      await payment.requestPayment('카드');
      
      return { success: true, paymentKey: 'toss_payment_' + Date.now() };
    } catch (error) {
      devError('결제 요청 실패:', error);
      throw new Error('결제 요청에 실패했습니다.');
    }
  }
  
  // 토스페이먼츠 SDK 동적 로드
  private async loadTossPaymentsSDK(): Promise<void> {
    return new Promise((resolve, reject) => {
      if ((window as any).TossPayments) {
        resolve();
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://js.tosspayments.com/v1/payment';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('토스페이먼츠 SDK 로드 실패'));
      document.head.appendChild(script);
    });
  }
}

// 싱글톤 인스턴스
export const tossPaymentsBrandPay = new TossPaymentsBrandPay(TOSS_PAYMENTS_CLIENT_KEY);
